// VOY-417 §1 — Travel personality (archetype) — INFERRED, read-only here.
//
// Reads `user_agent_profile.archetype` (VOY-503 schema): name, narrative,
// pace, budget, party. Archetype name with serif-italic accent, 2-sentence
// narrative, three labeled quick-attributes. "UPDATED N DAYS AGO" from
// updated_at.
//
// Correction of the underlying signals happens in §3 (style attribute
// Correct) and §8 (Memory Editor). This section is intentionally a
// readout — the archetype is a derived label, not a single dimension you
// can correct in isolation.
//
// Empty state: muted "Take a few searches and Voy will start building
// your traveler profile." Same dimensions, no layout shift on async fill.

import type { AgentProfile } from '@/types/profile'

export interface TravelPersonalitySectionProps {
  agentProfile: AgentProfile | null
  loading: boolean
}

function daysAgo(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const ms = Date.now() - new Date(iso).getTime()
    if (ms < 0) return 'JUST NOW'
    const days = Math.floor(ms / (1000 * 60 * 60 * 24))
    if (days === 0) return 'TODAY'
    if (days === 1) return '1 DAY AGO'
    return `${days} DAYS AGO`
  } catch {
    return ''
  }
}

function archetypeMonogram(name: string | null | undefined): string {
  if (!name) return '?'
  const first = name.trim()[0]
  return first ? first.toUpperCase() : '?'
}

export function TravelPersonalitySection({ agentProfile, loading }: TravelPersonalitySectionProps) {
  const archetype = agentProfile?.archetype ?? null
  const updatedLabel = daysAgo(agentProfile?.updated_at)
  const hasData = Boolean(archetype?.name)

  return (
    <section className="card" aria-labelledby="personality-h">
      <div className="card-h">
        <h3 id="personality-h">Travel personality</h3>
        {hasData && updatedLabel && <span className="meta">UPDATED {updatedLabel}</span>}
        {!hasData && !loading && <span className="meta">NOT ENOUGH DATA YET</span>}
      </div>

      {loading ? (
        <div className="you-personality-skeleton" aria-busy="true">
          <div className="you-archetype-tile" aria-hidden="true" />
          <div className="you-skeleton-lines">
            <span /><span /><span />
          </div>
        </div>
      ) : hasData ? (
        <div className="you-personality">
          <div className="you-archetype-tile" aria-hidden="true">
            {archetypeMonogram(archetype?.name)}
          </div>
          <div className="you-personality-body">
            <h2 className="you-archetype-name">
              <span className="serif-i">{archetype?.name}</span>
            </h2>
            {archetype?.narrative && (
              <p className="you-archetype-narrative">{archetype.narrative}</p>
            )}
            <div className="you-archetype-attrs">
              <ArchetypeAttr label="PACE" value={archetype?.pace} />
              <ArchetypeAttr label="BUDGET" value={archetype?.budget} />
              <ArchetypeAttr label="PARTY" value={archetype?.party} />
            </div>
          </div>
        </div>
      ) : (
        <div className="you-empty-state">
          <p className="you-empty-headline">Voy is still getting to know you.</p>
          <p className="you-empty-sub">
            Take a few searches, complete the quiz on Profile, or chat with the agent — your travel personality
            shows up here once Voy has enough signal.
          </p>
        </div>
      )}
    </section>
  )
}

function ArchetypeAttr({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="you-archetype-attr">
      <span className="lbl">{label}</span>
      <span className="num">{value || '—'}</span>
    </div>
  )
}
